import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, isStaff } from "@/context/AuthContext";
import { PublicLayout } from "@/components/Layout";
import { Loader, EmptyState } from "@/components/common";
import Home from "@/pages/Home";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import Standings from "@/pages/Standings";
import { FinalsList, ResultDetail } from "@/pages/Results";
import Clans, { ClanProfile } from "@/pages/Clans";
import News, { NewsDetail, Alliance, Staff, Regulations } from "@/pages/Content";
import { Login, Register, ForgotPassword, ResetPassword, VerifyEmail } from "@/pages/Auth";
import Area from "@/pages/Area";
import DashboardLayout from "@/dashboard/DashboardLayout";
import { Overview, AdminUsers, AdminCorrections, AdminAudit, AdminApplications } from "@/dashboard/Admin";
import AdminEvents from "@/dashboard/AdminEvents";
import EventManage from "@/dashboard/EventManage";
import AdminClans from "@/dashboard/AdminClans";
import { AdminNews, AdminRegulations, AdminContent } from "@/dashboard/Editorial";

function ScrollTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 600);
    return () => clearTimeout(t);
  }, [pathname, hash]);
  return null;
}

function StaffOnly({ children, superOnly }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isStaff(user) || (superOnly && user.role !== "super_admin")) return <div className="p-10"><EmptyState title="Accesso negato" text="Non hai i permessi per accedere a questa sezione." testId="access-denied" /></div>;
  return children;
}

const P = (el) => <PublicLayout>{el}</PublicLayout>;

function NotFound() {
  return <div className="max-w-3xl mx-auto px-5 pt-32"><EmptyState title="Pagina non trovata" text="La pagina richiesta non esiste." testId="not-found" /></div>;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollTop />
          <Routes>
            <Route path="/" element={P(<Home />)} />
            <Route path="/eventi" element={P(<Events />)} />
            <Route path="/eventi/:slug" element={P(<EventDetail />)} />
            <Route path="/classifiche" element={P(<Standings />)} />
            <Route path="/classifiche/:slug" element={P(<Standings />)} />
            <Route path="/risultati" element={P(<FinalsList />)} />
            <Route path="/risultati/:slug" element={P(<ResultDetail />)} />
            <Route path="/archivio" element={P(<FinalsList archive />)} />
            <Route path="/clan" element={P(<Clans />)} />
            <Route path="/clan/:slug" element={P(<ClanProfile />)} />
            <Route path="/news" element={P(<News />)} />
            <Route path="/news/:slug" element={P(<NewsDetail />)} />
            <Route path="/alleanza" element={P(<Alliance />)} />
            <Route path="/staff" element={P(<Staff />)} />
            <Route path="/regolamenti" element={P(<Regulations />)} />
            <Route path="/login" element={P(<Login />)} />
            <Route path="/registrazione" element={P(<Register />)} />
            <Route path="/password-dimenticata" element={P(<ForgotPassword />)} />
            <Route path="/reimposta-password" element={P(<ResetPassword />)} />
            <Route path="/verifica-email" element={P(<VerifyEmail />)} />
            <Route path="/area" element={P(<Area />)} />
            <Route path="/dashboard" element={<StaffOnly><DashboardLayout /></StaffOnly>}>
              <Route index element={<Overview />} />
              <Route path="eventi" element={<AdminEvents />} />
              <Route path="eventi/:id" element={<EventManage />} />
              <Route path="clan" element={<AdminClans />} />
              <Route path="utenti" element={<StaffOnly superOnly><AdminUsers /></StaffOnly>} />
              <Route path="news" element={<AdminNews />} />
              <Route path="regolamenti" element={<AdminRegulations />} />
              <Route path="contenuti" element={<StaffOnly superOnly><AdminContent /></StaffOnly>} />
              <Route path="correzioni" element={<AdminCorrections />} />
              <Route path="candidature" element={<AdminApplications />} />
              <Route path="registro" element={<StaffOnly superOnly><AdminAudit /></StaffOnly>} />
            </Route>
            <Route path="*" element={P(<NotFound />)} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="dark" position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}
